import { motion } from 'framer-motion'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'

const phases = [
  {
    step: '01',
    title: 'Free Consultation',
    body: "Your transformation begins with a free consultation to uncover what's got you stuck. This conversation helps us determine if we're the right fit for working together.",
    color: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  {
    step: '02',
    title: 'Map Your Inner World',
    body: "We map your inner landscape to identify competing priorities and conflicts — gaining clarity about what's creating tension and what you truly want.",
    color: 'text-indigo-400',
    bg: 'bg-indigo-900/30',
  },
  {
    step: '03',
    title: 'Create Alignment',
    body: "Our core work focuses on creating understanding and harmony between different aspects of yourself. As these parts align, you'll experience reduced resistance and self-sabotage.",
    color: 'text-violet-400',
    bg: 'bg-violet-900/30',
  },
  {
    step: '04',
    title: 'Move Forward',
    body: 'With your inner world working in concert, practical steps forward emerge naturally — making sustainable progress not just possible, but natural.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-900/30',
  },
]

export default function Services() {
  return (
    <section id="services-detail" className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden">
      {/* Dot texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left: copy */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3"
            >
              One-on-One Coaching
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="text-3xl sm:text-4xl font-bold text-white mb-6"
            >
              A Structured Path to Clarity and Forward Motion
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-slate-400 leading-relaxed mb-6"
            >
              Most people don't seek a coach because everything is going great. Something's stuck.
              Something's shifting. Something's missing. You've tried thinking your way through it.
              And while that has its place, it didn't quite get you moving.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-slate-400 leading-relaxed mb-8"
            >
              That's where coaching comes in — not as another quick fix, but as a structured path
              toward lasting clarity and momentum. Virtual sessions with professionals worldwide.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <CalendlyBtn
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white border-transparent"
              >
                Book Free Consultation
              </CalendlyBtn>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium bg-transparent border border-white/20 text-white hover:bg-white/10 transition-colors"
              >
                See All Services <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>

          {/* Right: phases */}
          <div className="space-y-4">
            {phases.map((phase, i) => (
              <motion.div
                key={phase.step}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 flex gap-4"
              >
                <div className={`${phase.bg} ${phase.color} w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm`}>
                  {phase.step}
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{phase.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{phase.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
